import { useEffect, useRef, useState, useCallback } from "react";
import { toast } from "sonner";
import { Trash2, Copy, Upload } from "lucide-react";
import api, { formatApiError } from "@/lib/api";

const API = process.env.REACT_APP_BACKEND_URL;

const inputCls =
  "w-full bg-transparent border-0 border-b border-white/20 rounded-none px-0 py-3 text-[#F5F4F0] placeholder:text-[#525252] focus:outline-none focus:border-[#C9A26B] transition-colors duration-300 text-sm";

const formatBytes = (n) => {
  if (!n) return "0 MB";
  if (n >= 1099511627776) return `${(n / 1099511627776).toFixed(2)} TB`;
  if (n >= 1073741824) return `${(n / 1073741824).toFixed(2)} GB`;
  if (n >= 1048576) return `${(n / 1048576).toFixed(1)} MB`;
  return `${Math.max(1, Math.round(n / 1024))} KB`;
};

const Fact = ({ label, value, testid }) => (
  <div>
    <p className="text-[10px] tracking-[0.3em] uppercase text-[#525252] mb-1.5">{label}</p>
    <p className="text-sm text-[#F5F4F0] break-all" data-testid={testid}>{value || "—"}</p>
  </div>
);

export const AdminGalleries = () => {
  const [galleries, setGalleries] = useState(null);
  const [form, setForm] = useState({ couple_name: "", bride_email: "", groom_email: "", wedding_date: "", photos_ready_date: "", location: "", notes: "" });
  const [creating, setCreating] = useState(false);
  const [uploadingFor, setUploadingFor] = useState(null);
  const fileRefs = useRef({});
  const coverRefs = useRef({});
  const [detailFor, setDetailFor] = useState(null);
  const [detail, setDetail] = useState(null);
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [coverVer, setCoverVer] = useState(0);
  const [coverBusy, setCoverBusy] = useState(false);

  const refreshDetail = async (gid) => {
    const { data } = await api.get(`/admin/galleries/${gid}`);
    setDetail(data);
  };

  const toggleDetail = async (g) => {
    if (detailFor === g.id) {
      setDetailFor(null);
      setDetail(null);
      setEditing(false);
      return;
    }
    setDetailFor(g.id);
    setDetail(null);
    setEditing(false);
    try {
      await refreshDetail(g.id);
    } catch (err) {
      toast.error(formatApiError(err));
      setDetailFor(null);
    }
  };

  const startEdit = () => {
    setEditForm({
      couple_name: detail.couple_name || "",
      bride_email: detail.bride_email || "",
      groom_email: detail.groom_email || "",
      wedding_date: detail.wedding_date || "",
      photos_ready_date: detail.photos_ready_date || "",
      location: detail.location || "",
      notes: detail.notes || "",
    });
    setEditing(true);
  };

  const saveEdit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {};
      Object.entries(editForm).forEach(([k, v]) => { if (v && v.trim()) payload[k] = v.trim(); });
      await api.put(`/galleries/${detailFor}`, payload);
      toast.success("Gallery updated");
      setEditing(false);
      await refreshDetail(detailFor);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const deletePhoto = async (photoId, filename) => {
    if (!window.confirm(`Delete ${filename}? This cannot be undone.`)) return;
    try {
      await api.delete(`/photos/${photoId}`);
      toast.success("Photo deleted");
      await refreshDetail(detailFor);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/galleries");
      setGalleries(data);
    } catch (e) {
      toast.error("Could not load galleries");
      setGalleries([]);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const create = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      const { data } = await api.post("/galleries", form);
      const emailed = [form.bride_email, form.groom_email].filter(Boolean);
      toast.success(emailed.length ? `Gallery created — invites sent to ${emailed.join(" & ")}` : `Gallery created — guest code ${data.code} · direct key ${data.direct_code}`);
      setForm({ couple_name: "", bride_email: "", groom_email: "", wedding_date: "", photos_ready_date: "", location: "", notes: "" });
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setCreating(false);
    }
  };

  const upload = async (galleryId, files) => {
    if (!files?.length) return;
    setUploadingFor(galleryId);
    try {
      const fd = new FormData();
      Array.from(files).forEach((f) => fd.append("files", f));
      const { data } = await api.post(`/galleries/${galleryId}/photos`, fd);
      toast.success(`${data.uploaded} photo(s) uploaded`);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setUploadingFor(null);
      if (fileRefs.current[galleryId]) fileRefs.current[galleryId].value = "";
    }
  };

  const uploadCover = async (galleryId, file) => {
    if (!file) return;
    setUploadingFor(galleryId);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/galleries/${galleryId}/cover`, fd);
      toast.success("Wallpaper set — guests will see it at the top of the gallery");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setUploadingFor(null);
      if (coverRefs.current[galleryId]) coverRefs.current[galleryId].value = "";
    }
  };

  const setCoverFromPhoto = async (photoId) => {
    setCoverBusy(true);
    try {
      await api.post(`/galleries/${detailFor}/cover-from-photo/${photoId}`);
      toast.success("Wallpaper updated — this photo now opens the gallery");
      setCoverVer((v) => v + 1);
      await refreshDetail(detailFor);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setCoverBusy(false);
    }
  };

  const setCoverPosition = async (pos) => {
    setCoverBusy(true);
    try {
      await api.put(`/galleries/${detailFor}`, { cover_position: pos });
      setCoverVer((v) => v + 1);
      await refreshDetail(detailFor);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setCoverBusy(false);
    }
  };

  const remove = async (id, name) => {    if (!window.confirm(`Delete the gallery for ${name}? This cannot be undone.`)) return;
    try {
      await api.delete(`/galleries/${id}`);
      toast.success("Gallery deleted");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const copyLink = (g) => {
    const link = g.token ? `${window.location.origin}/gallery/${g.token}` : `${window.location.origin}/gallery?code=${g.code}`;
    navigator.clipboard?.writeText(link).then(
      () => toast.success("Link copied — send it to the couple"),
      () => toast.info(link)
    );
  };

  const toggleRelease = async (g) => {
    if (!g.released && !window.confirm(`Release ${g.couple_name}'s gallery? The couple and every joined guest get an email immediately.`)) return;
    try {
      await api.post(`/galleries/${g.id}/release`, { released: !g.released });
      toast.success(g.released ? "Gallery hidden again" : "Gallery released — everyone has been emailed");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const toggleFaceMatch = async (g) => {
    if (!g.face_match && !window.confirm(`Turn on face matching for ${g.couple_name}? Guests will only see the photos they appear in (they register with a selfie). Couples always see everything.`)) return;
    try {
      await api.post(`/galleries/${g.id}/face-match`, { enabled: !g.face_match });
      toast.success(g.face_match ? "Face matching off — everyone sees the full gallery" : "Face matching on — guests see only their own photos");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div data-testid="admin-galleries">
      <form onSubmit={create} className="grid grid-cols-1 sm:grid-cols-3 gap-8 items-end mb-6 border border-white/10 p-8" data-testid="gallery-create-form">
        <div>
          <label className="text-[10px] tracking-[0.3em] uppercase text-[#525252]">Couple names *</label>
          <input required data-testid="gallery-couple-input" value={form.couple_name} onChange={(e) => setForm((f) => ({ ...f, couple_name: e.target.value }))} placeholder="Elena & Marco" className={inputCls} />
        </div>
        <div>
          <label className="text-[10px] tracking-[0.3em] uppercase text-[#525252]">Bride email</label>
          <input type="email" data-testid="gallery-bride-email-input" value={form.bride_email} onChange={(e) => setForm((f) => ({ ...f, bride_email: e.target.value }))} placeholder="bride@love.com" className={inputCls} />
        </div>
        <div>
          <label className="text-[10px] tracking-[0.3em] uppercase text-[#525252]">Groom email</label>
          <input type="email" data-testid="gallery-groom-email-input" value={form.groom_email} onChange={(e) => setForm((f) => ({ ...f, groom_email: e.target.value }))} placeholder="groom@love.com" className={inputCls} />
        </div>
        <div>
          <label className="text-[10px] tracking-[0.3em] uppercase text-[#525252]">Wedding date</label>
          <input type="date" data-testid="gallery-date-input" value={form.wedding_date} onChange={(e) => setForm((f) => ({ ...f, wedding_date: e.target.value }))} className={inputCls} />
        </div>
        <div>
          <label className="text-[10px] tracking-[0.3em] uppercase text-[#525252]">Photos ready by — shown to direct-key guests</label>
          <input type="date" data-testid="gallery-ready-date-input" value={form.photos_ready_date} onChange={(e) => setForm((f) => ({ ...f, photos_ready_date: e.target.value }))} className={inputCls} />
        </div>
        <div>
          <label className="text-[10px] tracking-[0.3em] uppercase text-[#525252]">Location</label>
          <input data-testid="gallery-location-input" value={form.location} onChange={(e) => setForm((f) => ({ ...f, location: e.target.value }))} placeholder="Lake Como, Italy" className={inputCls} />
        </div>
        <div>
          <label className="text-[10px] tracking-[0.3em] uppercase text-[#525252]">Notes</label>
          <input data-testid="gallery-notes-input" value={form.notes} onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))} placeholder="Lake Como weekend" className={inputCls} />
        </div>
        <button type="submit" disabled={creating} data-testid="gallery-create-btn"
          className="text-xs tracking-[0.3em] uppercase bg-[#C9A26B] text-[#0A0A0A] px-6 py-4 hover:bg-transparent hover:text-[#C9A26B] border border-[#C9A26B] transition-colors duration-400 disabled:opacity-50">
          {creating ? "Creating…" : "Create Gallery"}
        </button>
      </form>

      {galleries === null ? (
        <p className="text-[#525252]">Loading…</p>
      ) : galleries.length === 0 ? (
        <p className="text-[#A3A3A3]" data-testid="galleries-empty">No galleries yet. Create one above, upload the couple's photos, and send them the link.</p>
      ) : (
        <div className="flex flex-col gap-6" data-testid="galleries-list">
          {galleries.map((g) => (
            <div key={g.id} className="border border-white/10" data-testid={`gallery-row-${g.code}`}>
            <div className="p-6 flex flex-wrap items-center gap-6 justify-between">
              <div className="cursor-pointer group flex-1 min-w-[220px]" onClick={() => toggleDetail(g)} data-testid={`gallery-open-${g.code}`} title="Click to open this wedding">
                <p className="font-serif text-xl text-[#F5F4F0] group-hover:text-[#C9A26B] transition-colors duration-300">{g.couple_name}</p>
                <p className="text-xs text-[#737373] mt-1">
                  Code <span className="text-[#C9A26B] tracking-[0.2em]">{g.code}</span>
                  {g.wedding_date ? ` · ${g.wedding_date}` : ""} · {g.photo_count} photo{g.photo_count === 1 ? "" : "s"}
                  {g.favorite_count > 0 ? ` · ${g.favorite_count} ♥ favorited` : ""}
                  {" · "}
                  <span className={g.released ? "text-[#C9A26B]" : "text-[#737373]"} data-testid={`gallery-status-${g.code}`}>
                    {g.released ? "Released" : "Hidden"}
                  </span>
                </p>
                {g.events?.length > 0 && (
                  <div className="mt-3 flex flex-col gap-2" data-testid={`gallery-events-${g.code}`}>
                    {g.events.map((ev) => (
                      <p key={ev.id} className="text-xs text-[#A3A3A3] leading-relaxed">
                        <span className="text-[#F5F4F0]">{ev.title}</span>
                        {ev.event_date ? ` · ${ev.event_date}` : ""}{ev.time ? ` ${ev.time}` : ""}
                        {ev.description ? ` — ${ev.description}` : ""}
                      </p>
                    ))}
                  </div>
                )}
              </div>
              <div className="flex items-center gap-3 flex-wrap">
                <input
                  type="file" accept="image/*" multiple className="hidden"
                  ref={(el) => (fileRefs.current[g.id] = el)}
                  onChange={(e) => upload(g.id, e.target.files)}
                  data-testid={`gallery-file-input-${g.code}`}
                />
                <button onClick={() => fileRefs.current[g.id]?.click()} disabled={uploadingFor === g.id} data-testid={`gallery-upload-btn-${g.code}`}
                  className="flex items-center gap-2 text-[10px] tracking-[0.2em] uppercase border border-[#C9A26B]/50 px-4 py-2.5 text-[#C9A26B] hover:bg-[#C9A26B] hover:text-[#0A0A0A] transition-colors duration-300 disabled:opacity-50">
                  <Upload className="w-3.5 h-3.5" /> {uploadingFor === g.id ? "Uploading…" : "Upload Photos"}
                </button>
                <input
                  type="file" accept="image/*" className="hidden"
                  ref={(el) => (coverRefs.current[g.id] = el)}
                  onChange={(e) => uploadCover(g.id, e.target.files[0])}
                  data-testid={`gallery-cover-input-${g.code}`}
                />
                <button onClick={() => coverRefs.current[g.id]?.click()} disabled={uploadingFor === g.id} data-testid={`gallery-cover-btn-${g.code}`}
                  className="flex items-center gap-2 text-[10px] tracking-[0.2em] uppercase border border-white/30 px-4 py-2.5 text-[#F5F4F0] hover:bg-white/10 transition-colors duration-300 disabled:opacity-50">
                  Set Wallpaper
                </button>
                <button onClick={() => toggleFaceMatch(g)} data-testid={`gallery-facematch-btn-${g.code}`}
                  className={`flex items-center gap-2 text-[10px] tracking-[0.2em] uppercase px-4 py-2.5 border transition-colors duration-300 ${
                    g.face_match
                      ? "border-[#C9A26B] text-[#C9A26B]"
                      : "border-white/30 text-[#A3A3A3] hover:text-[#F5F4F0]"
                  }`}>
                  Face Match {g.face_match ? "On" : "Off"}
                </button>
                <button onClick={() => toggleRelease(g)} data-testid={`gallery-release-btn-${g.code}`}
                  className={`flex items-center gap-2 text-[10px] tracking-[0.2em] uppercase px-4 py-2.5 border transition-colors duration-300 ${
                    g.released
                      ? "border-white/30 text-[#A3A3A3] hover:text-[#F5F4F0]"
                      : "border-[#C9A26B] bg-[#C9A26B] text-[#0A0A0A] hover:bg-transparent hover:text-[#C9A26B]"
                  }`}>
                  {g.released ? "Hide" : "Release"}
                </button>
                <button onClick={() => toggleDetail(g)} data-testid={`gallery-detail-btn-${g.code}`}
                  className={`flex items-center gap-2 text-[10px] tracking-[0.2em] uppercase px-4 py-2.5 border transition-colors duration-300 ${
                    detailFor === g.id
                      ? "border-[#C9A26B] text-[#C9A26B]"
                      : "border-white/30 text-[#F5F4F0] hover:bg-white/10"
                  }`}>
                  {detailFor === g.id ? "Close" : "Details"}
                </button>
                <button onClick={() => copyLink(g)} data-testid={`gallery-copy-btn-${g.code}`}
                  className="flex items-center gap-2 text-[10px] tracking-[0.2em] uppercase border border-white/30 px-4 py-2.5 text-[#F5F4F0] hover:bg-white/10 transition-colors duration-300">
                  <Copy className="w-3.5 h-3.5" /> Copy Link
                </button>
                <button onClick={() => remove(g.id, g.couple_name)} data-testid={`gallery-delete-btn-${g.code}`}
                  className="flex items-center gap-2 text-[10px] tracking-[0.2em] uppercase border border-white/10 px-4 py-2.5 text-[#737373] hover:text-red-400 hover:border-red-400/40 transition-colors duration-300">
                  <Trash2 className="w-3.5 h-3.5" /> Delete
                </button>
              </div>
            </div>
            {detailFor === g.id && (
              <div className="border-t border-white/10 p-6" data-testid={`gallery-detail-${g.code}`}>
                {!detail || detail.id !== g.id ? (
                  <p className="text-[#525252] text-sm">Loading details…</p>
                ) : (
                  <div className="flex flex-col gap-10">
                    <div className="flex justify-end">
                      {!editing && (
                        <button onClick={startEdit} data-testid={`gallery-edit-btn-${g.code}`}
                          className="text-[10px] tracking-[0.2em] uppercase border border-[#C9A26B]/60 px-4 py-2.5 text-[#C9A26B] hover:bg-[#C9A26B] hover:text-[#0A0A0A] transition-colors duration-300">
                          Edit Details
                        </button>
                      )}
                    </div>
                    {editing ? (
                      <form onSubmit={saveEdit} className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6 border border-[#C9A26B]/30 p-6" data-testid={`gallery-edit-form-${g.code}`}>
                        <div>
                          <label className="text-[10px] tracking-[0.3em] uppercase text-[#525252]">Couple names</label>
                          <input required data-testid="edit-couple-input" value={editForm.couple_name} onChange={(e) => setEditForm((f) => ({ ...f, couple_name: e.target.value }))} className={inputCls} />
                        </div>
                        <div>
                          <label className="text-[10px] tracking-[0.3em] uppercase text-[#525252]">Wedding date</label>
                          <input type="date" data-testid="edit-date-input" value={editForm.wedding_date} onChange={(e) => setEditForm((f) => ({ ...f, wedding_date: e.target.value }))} className={inputCls} />
                        </div>
                        <div>
                          <label className="text-[10px] tracking-[0.3em] uppercase text-[#525252]">Photos ready by</label>
                          <input type="date" data-testid="edit-ready-date-input" value={editForm.photos_ready_date} onChange={(e) => setEditForm((f) => ({ ...f, photos_ready_date: e.target.value }))} className={inputCls} />
                        </div>
                        <div>
                          <label className="text-[10px] tracking-[0.3em] uppercase text-[#525252]">Bride email</label>
                          <input type="email" data-testid="edit-bride-input" value={editForm.bride_email} onChange={(e) => setEditForm((f) => ({ ...f, bride_email: e.target.value }))} placeholder="blank = keep current" className={inputCls} />
                        </div>
                        <div>
                          <label className="text-[10px] tracking-[0.3em] uppercase text-[#525252]">Groom email</label>
                          <input type="email" data-testid="edit-groom-input" value={editForm.groom_email} onChange={(e) => setEditForm((f) => ({ ...f, groom_email: e.target.value }))} placeholder="blank = keep current" className={inputCls} />
                        </div>
                        <div>
                          <label className="text-[10px] tracking-[0.3em] uppercase text-[#525252]">Location</label>
                          <input data-testid="edit-location-input" value={editForm.location} onChange={(e) => setEditForm((f) => ({ ...f, location: e.target.value }))} placeholder="Lake Como, Italy" className={inputCls} />
                        </div>
                        <div>
                          <label className="text-[10px] tracking-[0.3em] uppercase text-[#525252]">Notes</label>
                          <input data-testid="edit-notes-input" value={editForm.notes} onChange={(e) => setEditForm((f) => ({ ...f, notes: e.target.value }))} className={inputCls} />
                        </div>
                        <div className="md:col-span-2 flex gap-4">
                          <button type="submit" disabled={saving} data-testid="edit-save-btn"
                            className="text-xs tracking-[0.3em] uppercase bg-[#C9A26B] text-[#0A0A0A] px-6 py-3.5 hover:bg-transparent hover:text-[#C9A26B] border border-[#C9A26B] transition-colors duration-400 disabled:opacity-50">
                            {saving ? "Saving…" : "Save Changes"}
                          </button>
                          <button type="button" onClick={() => setEditing(false)} data-testid="edit-cancel-btn"
                            className="text-xs tracking-[0.3em] uppercase border border-white/20 px-6 py-3.5 text-[#737373] hover:text-[#F5F4F0] transition-colors duration-300">
                            Cancel
                          </button>
                        </div>
                      </form>
                    ) : (
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-x-8 gap-y-6">
                      <Fact label="Bride email" value={detail.bride_email} testid={`detail-bride-${g.code}`} />
                      <Fact label="Groom email" value={detail.groom_email} testid={`detail-groom-${g.code}`} />
                      <div>
                        <p className="text-[10px] tracking-[0.3em] uppercase text-[#525252] mb-1.5">Guest code — with registration</p>
                        <p className="text-sm text-[#F5F4F0]" data-testid={`detail-code-${g.code}`}>{detail.code}</p>
                      </div>
                      <div>
                        <p className="text-[10px] tracking-[0.3em] uppercase text-[#525252] mb-1.5">Direct key — instant entry, no account</p>
                        <div className="flex items-center gap-3">
                          <p className="text-sm text-[#C9A26B] tracking-wider" data-testid={`detail-direct-${g.code}`}>{detail.direct_code || "—"}</p>
                          {detail.direct_code && (
                            <button onClick={() => { navigator.clipboard.writeText(detail.direct_code); toast.success("Direct key copied"); }}
                              data-testid={`detail-direct-copy-${g.code}`}
                              className="text-[9px] tracking-[0.2em] uppercase text-[#737373] hover:text-[#C9A26B] border border-white/15 px-2 py-1 transition-colors duration-300">
                              Copy
                            </button>
                          )}
                        </div>
                      </div>
                      <Fact label="Wedding date" value={detail.wedding_date} />
                      <Fact label="Photos ready by" value={detail.photos_ready_date} testid={`detail-ready-date-${g.code}`} />
                      <Fact label="Location" value={detail.location} testid={`detail-location-${g.code}`} />
                      <Fact label="Notes" value={detail.notes} />
                      <Fact label="Created" value={new Date(detail.created_at).toLocaleDateString()} />
                      <Fact label="Status" value={detail.released ? "Released — visible to clients" : "Hidden — not yet released"} testid={`detail-status-${g.code}`} />
                      <Fact label="Face match" value={detail.face_match ? "On — guests see only their photos" : "Off"} />
                      <Fact label="Photos" value={`${detail.photo_count}`} />
                      <Fact label="Storage used" value={formatBytes(detail.storage_bytes)} testid={`detail-storage-${g.code}`} />
                      <Fact label="Favorites" value={`${detail.favorite_count} ♥`} />
                      <Fact label="Guests joined" value={`${detail.guests.length}`} testid={`detail-guests-count-${g.code}`} />
                    </div>
                    )}
                    <div data-testid={`wallpaper-manager-${g.code}`}>
                      <p className="text-[10px] tracking-[0.3em] uppercase text-[#525252] mb-4">Wallpaper — what guests see first</p>
                      <div className="relative h-52 md:h-64 overflow-hidden border border-white/10 bg-[#121212]">
                        {detail.has_cover ? (
                          <>
                            <img src={`${API}/api/galleries/${encodeURIComponent(detail.code)}/cover?v=${coverVer}`} alt="Gallery wallpaper preview"
                              className="w-full h-full object-cover" style={{ objectPosition: `center ${detail.cover_position ?? 50}%` }}
                              data-testid={`wallpaper-preview-${g.code}`} />
                            <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-transparent to-transparent" />
                            <p className="absolute bottom-4 left-5 font-serif text-2xl text-[#F5F4F0]">{detail.couple_name}</p>
                          </>
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <p className="text-sm text-[#737373] px-6 text-center" data-testid={`wallpaper-empty-${g.code}`}>
                              No wallpaper yet — use "Set Wallpaper" above to upload one, or pick any photo below.
                            </p>
                          </div>
                        )}
                      </div>
                      {detail.has_cover && (
                        <div className="flex items-center gap-3 mt-4 flex-wrap">
                          <span className="text-[10px] tracking-[0.2em] uppercase text-[#525252]">Focus</span>
                          {[["Top", 15], ["Center", 50], ["Bottom", 85]].map(([label, pos]) => (
                            <button key={label} onClick={() => setCoverPosition(pos)} disabled={coverBusy}
                              data-testid={`wallpaper-pos-${label.toLowerCase()}-${g.code}`}
                              className={`text-[10px] tracking-[0.2em] uppercase px-4 py-2 border transition-colors duration-300 disabled:opacity-50 ${
                                (detail.cover_position ?? 50) === pos
                                  ? "border-[#C9A26B] text-[#C9A26B]"
                                  : "border-white/20 text-[#A3A3A3] hover:text-[#F5F4F0]"
                              }`}>
                              {label}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                    <div>
                      <p className="text-[10px] tracking-[0.3em] uppercase text-[#525252] mb-4">Joined guests</p>
                      {detail.guests.length === 0 ? (
                        <p className="text-sm text-[#737373]">No guests have joined yet.</p>
                      ) : (
                        <div className="flex flex-col divide-y divide-white/10 border-y border-white/10">
                          {detail.guests.map((gst, i) => (
                            <div key={i} className="py-3 flex items-center justify-between gap-4" data-testid={`detail-guest-${g.code}-${i}`}>
                              <div>
                                <p className="text-sm text-[#F5F4F0]">{gst.name}</p>
                                <p className="text-xs text-[#737373]">{gst.email}</p>
                              </div>
                              <p className="text-[10px] tracking-[0.2em] uppercase text-[#525252] whitespace-nowrap">
                                {gst.joined_at ? new Date(gst.joined_at).toLocaleDateString() : ""}
                                {gst.matched_photos != null ? ` · ${gst.matched_photos} matched` : ""}
                              </p>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                    <div>
                      <p className="text-[10px] tracking-[0.3em] uppercase text-[#525252] mb-4">Photos ({detail.photos.length})</p>
                      {detail.photos.length === 0 ? (
                        <p className="text-sm text-[#737373]">No photos uploaded yet.</p>
                      ) : (
                        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3" data-testid={`detail-photos-${g.code}`}>
                          {detail.photos.map((p) => (
                            <div key={p.id} className="relative group border border-white/10 bg-[#121212]" data-testid={`detail-photo-${p.id}`}>
                              <img src={`${API}/api/admin/photos/${p.id}`} alt={p.filename} loading="lazy"
                                className="w-full aspect-square object-cover" />
                              <button onClick={() => deletePhoto(p.id, p.filename)} data-testid={`detail-photo-delete-${p.id}`}
                                className="absolute top-2 right-2 bg-[#0A0A0A]/85 border border-white/20 text-[#F5F4F0] text-[9px] tracking-[0.2em] uppercase px-2.5 py-1.5 md:opacity-0 md:group-hover:opacity-100 hover:text-red-400 hover:border-red-400/50 transition-opacity duration-300">
                                Delete
                              </button>
                              <button onClick={() => setCoverFromPhoto(p.id)} disabled={coverBusy} data-testid={`detail-photo-cover-${p.id}`}
                                className="absolute top-2 left-2 bg-[#0A0A0A]/85 border border-[#C9A26B]/40 text-[#C9A26B] text-[9px] tracking-[0.2em] uppercase px-2.5 py-1.5 md:opacity-0 md:group-hover:opacity-100 hover:bg-[#C9A26B] hover:text-[#0A0A0A] transition-opacity duration-300 disabled:opacity-50">
                                Wallpaper
                              </button>
                              <p className="text-[9px] text-[#525252] px-2 py-1.5 truncate">{p.filename} · {formatBytes(p.size)}</p>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
